import { tokenManager } from "../utils/tokenManager";
import { getAuthDevApiBaseUrl } from "../api/authDevApiClient";
import { encryptPassword } from "../utils/encryption";
import {
  handleAuthApiFailure,
  messageForAuthCode,
  messageForLoginFailure,
} from "../utils/authSessionFailure.js";

function unwrapAuthPayload(response) {
  if (response?.data?.token) {
    return response.data;
  }
  return response;
}

/** Extract a user-facing message from auth API error JSON. */
function messageFromAuthErrorBody(errorData, httpStatus, forLogin = false) {
  const code = codeFromAuthErrorBody(errorData);
  const loginFailure = forLogin ? messageForLoginFailure(code) : null;
  if (loginFailure) return loginFailure;
  if (code === "ACCOUNT_INACTIVE" || code === "ROLE_INACTIVE") {
    return messageForAuthCode(code);
  }
  if (errorData && typeof errorData === "object") {
    const nested =
      errorData.error &&
      typeof errorData.error === "object" &&
      !Array.isArray(errorData.error)
        ? errorData.error
        : null;
    if (nested && typeof nested.message === "string" && nested.message.trim()) {
      return nested.message.trim();
    }
    if (typeof errorData.message === "string" && errorData.message.trim()) {
      return errorData.message.trim();
    }
    if (
      Array.isArray(errorData.errors) &&
      errorData.errors[0] &&
      typeof errorData.errors[0].message === "string" &&
      errorData.errors[0].message.trim()
    ) {
      return errorData.errors[0].message.trim();
    }
    if (typeof errorData.detail === "string" && errorData.detail.trim()) {
      return errorData.detail.trim();
    }
  }
  if (httpStatus === 401) return "Invalid email or password.";
  if (httpStatus === 403) return "You do not have permission to sign in.";
  if (httpStatus === 429) return "Too many attempts. Please try again later.";
  return "Login failed. Please try again.";
}

function codeFromAuthErrorBody(errorData) {
  if (errorData && typeof errorData === "object") {
    const nested =
      errorData.error &&
      typeof errorData.error === "object" &&
      !Array.isArray(errorData.error)
        ? errorData.error
        : null;
    if (nested && typeof nested.code === "string") return nested.code;
    if (typeof errorData.code === "string") return errorData.code;
  }
  return undefined;
}

// Auth-specific API request function
const authApiRequest = async (endpoint, options = {}) => {
  const token = tokenManager.getToken();
  const { headers: optionHeaders = {}, ...restOptions } = options;
  const isLoginRequest = String(endpoint).includes("/api/auth/login");

  const config = {
    ...restOptions,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      
      ...optionHeaders,
    },
  };

  const fullUrl = `${getAuthDevApiBaseUrl()}${endpoint}`;
  if (process.env.NODE_ENV === "development") {
    console.log("Making Auth API request:", {
      url: fullUrl,
      endpoint,
      hasAuthHeader: !!config.headers.Authorization,
    });
  }

  try {
    const response = await fetch(fullUrl, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (process.env.NODE_ENV === "development") {
        console.error("Auth API request failed:", {
          status: response.status,
          error: errorData,
        });
      }
      const code = codeFromAuthErrorBody(errorData);
      const message = messageFromAuthErrorBody(errorData, response.status, isLoginRequest);

      if (isLoginRequest) {
        const error = new Error(message);
        error.status = response.status;
        error.code = code;
        error.data = errorData;
        error.errors = errorData.errors || [];
        throw error;
      }

      if (handleAuthApiFailure(response.status, code, message, errorData)) {
        const error = new Error(message);
        error.status = response.status;
        error.code = code;
        throw error;
      }

      const error = new Error(message);
      error.status = response.status;
      error.code = code;
      error.data = errorData;
      error.errors = errorData.errors || [];
      throw error;
    }

    return await response.json();
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("Auth API request failed:", error);
    }
    throw error;
  }
};

/**
 * Authentication service for handling login, logout, and registration
 */
export const authService = {
  /**
   * Send OTP to email
   * @param {string} email - User email
   * @returns {Promise} OTP response
   */
  sendOtp: async ({ email }) => {
    try {
      const response = await authApiRequest("/auth/sendOtp", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Verify OTP
   * @param {string} email - User email
   * @param {string} otp - OTP code
   * @returns {Promise} Verification response
   */
  verifyOtp: async ({ email, otp }) => {
    try {
      const response = await authApiRequest("/auth/verifyOtp", {
        method: "POST",
        body: JSON.stringify({ email, otp }),
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Reset Password
   * @param {string} email - User email
   * @param {string} newPassword - New password
   * @param {string} confirmNewPassword - Confirm new password
   * @param {string} passwordResetToken - Password reset token
   * @param {string} otp - OTP code
   * @returns {Promise} Reset password response
   */
  resetPassword: async ({
    email,
    newPassword,
    confirmNewPassword,
    passwordResetToken,
    otp,
  }) => {
    try {
      const response = await authApiRequest("/auth/resetPassword", {
        method: "POST",
        body: JSON.stringify({
          email,
          newPassword: encryptPassword(newPassword),
          confirmNewPassword: encryptPassword(confirmNewPassword),
          passwordResetToken,
          otp,
        }),
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} Login response with user data and token
   */
  login: async (email, password) => {
    try {
      const response = await authApiRequest("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password: encryptPassword(password) }),
      });
      return unwrapAuthPayload(response);
    } catch (error) {
      throw error;
    }
  },
  refreshUser: async (token) => {
    try {
      const response = await authApiRequest("/auth/refreshUser", {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
  /**
   * Register new user
   * @param {string} name - User name
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} Registration response
   */
  register: async (name, email, password) => {
    try {
      const response = await authApiRequest("/auth/register", {
        method: "POST",
        body: JSON.stringify({ name, email, password: encryptPassword(password) }),
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Logout current user
   * @returns {Promise} Logout response
   */
  logout: async () => {
    const endpoints = ["/api/account/logout", "/api/auth/logout"];
    for (const endpoint of endpoints) {
      try {
        const response = await authApiRequest(endpoint, { method: "POST" });
        return unwrapAuthPayload(response) ?? {};
      } catch (error) {
        // Route missing or token already invalid — try next / fall through to client logout.
        if (error.status === 404 || error.status === 401) continue;
        console.warn(`Logout API ${endpoint} failed:`, error.message);
      }
    }
    return {};
  },

  /**
   * Get current user profile
   * @returns {Promise} User profile data
   */
  getProfile: async () => {
    try {
      const response = await authApiRequest("/auth/profile");
      return response;
    } catch (error) {
      throw error;
    }
  },
};
